// import { UserService } from '..'
import { UserService } from '..'
import {
  type Studio as GraphQLStudio,
  type CreateStudioInput as GraphQLCreateStudioInput,
  type UpdateStudioInput as GraphQLUpdateStudioInput,
  RequestBookingInput as GraphQLRequestBookingInput,
  DateTimeRange as GraphQLDateTimeRange,
  GetBookingCheckoutSessionInput
} from '../graphql/types'
import { GraphQLRequest } from '../request/api_request'
import { BookingStatus, IBooking } from '../types/booking'
import { type S3Object, type IDateRange, type IPagination, type IPaginatedList } from '../types/common'
import {
  RequestStudioBookingInput,
  type CreateStudioInput,
  type IStudio,
  type UpdateStudioInput,
  StudioStatus,
  StudioPendingAction,
  Availability
} from '../types/studio'
import { uuid } from '../utils'
import StorageObject, { StorageFolder } from '../utils/storage_object'
import Booking from './booking'
import {
  REQUEST_BOOKING_MUTATION,
  CREATE_STUDIO_MUTATION,
  GET_ALL_STUDIOS_QUERY,
  GET_STUDIO_BY_ID_QUERY,
  GET_USER_STUDIOS_QUERY,
  UPDATE_STUDIO_MUTATION,
  GET_OPEN_AVAILABILITIES_QUERY,
  RESPOND_TO_BOOKING_REQUEST_MUTATION,
  GET_BOOKING_CHECKOUT_SESSION_QUERY,
  GET_STUDIO_PENDING_ACTIONS_QUERY,
  GET_STUDIO_AVAILABILITY_QUERY
} from './queries'
import Studio from './studio'

export default class StudioService {
  createStudio = async (studioParams: CreateStudioInput): Promise<IStudio> => {
    const studioId = uuid()
    const studioPictures = await StudioService.saveStudioPictures(studioId, studioParams.info.studioPictures)
    const {
      timezone,
      MONDAY = [],
      TUESDAY = [],
      WEDNESDAY = [],
      THURSDAY = [],
      FRIDAY = [],
      SATURDAY = [],
      SUNDAY = []
    } = studioParams.availability.standardAvailability
    const stripeAccountOnboarded = (await UserService.getUserStripeAccount()).chargesEnabled ?? false
    const input: GraphQLCreateStudioInput = {
      id: studioId,
      status: stripeAccountOnboarded ? StudioStatus.ACTIVE : StudioStatus.PENDING_RESOLVE_ACTIONS,
      ownerUserId: studioParams.ownerUserId,
      standardAvailability: {
        timezone,
        MONDAY,
        TUESDAY,
        WEDNESDAY,
        THURSDAY,
        FRIDAY,
        SATURDAY,
        SUNDAY
      },
      availabilityExceptions: studioParams.availability.exceptions.map(exception => ({
        isAvailable: exception.isAvailable,
        timeRange: {
          startDate: exception.timeRange.startDate.toISOString(),
          endDate: exception.timeRange.endDate.toISOString()
        }
      })),
      info: {
        title: studioParams.info.title,
        minSessionMinutes: studioParams.info.minSessionMinutes,
        description: studioParams.info.description,
        rate: studioParams.info.rate,
        maxOccupancy: studioParams.info.maxOccupancy,
        location: studioParams.info.location,
        studioPictures
      }
    }
    if (!stripeAccountOnboarded) {
      input.pendingAdminActions = [StudioPendingAction.STRIPE_ACCOUNT_ONBOARDING]
    }
    try {
      const response = await GraphQLRequest({
        query: CREATE_STUDIO_MUTATION,
        variables: {
          input
        }
      })
      return new Studio(response.data.createStudio as GraphQLStudio)
    } catch (error) {
      // clean up pictures if studio creation fails
      const deletePictureOperations = studioPictures.map(async picture => { await StorageObject.deleteFile(picture) })
      await Promise.all(deletePictureOperations)
      throw error
    }
  }

  getStudioPendingActions = async (studioId: string): Promise<StudioPendingAction[]> => {
    const response = await GraphQLRequest({
      query: GET_STUDIO_PENDING_ACTIONS_QUERY,
      variables: {
        id: studioId
      }
    })
    console.log(response)
    const studio = response.data.getStudio as GraphQLStudio
    const actions = studio.pendingAdminActions ?? []
    return actions as StudioPendingAction[]
  }

  private readonly replaceStudioPicturesInStorage = async (studioId: string, newPictures: File[]): Promise<S3Object[]> => {
    const newPictureS3Objects = await StudioService.saveStudioPictures(studioId, newPictures)
    const existingStudio = await this.getStudioById(studioId);
    try {
      await Promise.all(existingStudio.info.studioPictures.map(async picture => {
        await picture.deleteFile()
      }))
    } catch (error) {
      // clean up pictures if studio creation fails
      const deletePictureOperations = newPictureS3Objects.map(async picture => { await StorageObject.deleteFile(picture) })
      await Promise.all(deletePictureOperations)
      throw error
    }
    return newPictureS3Objects
  }

  updateStudio = async (studioId: string, newStudio: UpdateStudioInput): Promise<IStudio> => {
    if (newStudio.info.studioPictures.length === 0) {
      throw new Error('Cannot update studio with no pictures')
    }
    const studioPictures = await this.replaceStudioPicturesInStorage(studioId, newStudio.info.studioPictures)
    const {
      timezone,
      MONDAY = [],
      TUESDAY = [],
      WEDNESDAY = [],
      THURSDAY = [],
      FRIDAY = [],
      SATURDAY = [],
      SUNDAY = []
    } = newStudio.availability.standardAvailability
    const updateInput: GraphQLUpdateStudioInput = {
      id: studioId,
      standardAvailability: {
        timezone,
        MONDAY,
        TUESDAY,
        WEDNESDAY,
        THURSDAY,
        FRIDAY,
        SATURDAY,
        SUNDAY
      },
      availabilityExceptions: newStudio.availability.exceptions.map(exception => ({
        isAvailable: exception.isAvailable,
        timeRange: {
          startDate: exception.timeRange.startDate.toISOString(),
          endDate: exception.timeRange.endDate.toISOString()
        }
      })),
      info: {
        title: newStudio.info.title,
        minSessionMinutes: newStudio.info.minSessionMinutes,
        description: newStudio.info.description,
        rate: newStudio.info.rate,
        maxOccupancy: newStudio.info.maxOccupancy,
        location: newStudio.info.location,
        studioPictures
      }
    }
    const response = await GraphQLRequest({
      query: UPDATE_STUDIO_MUTATION,
      variables: {
        input: updateInput
      }
    })
    return new Studio(response.data.updateStudio as GraphQLStudio)
  }

  getStudiosForStudioOwner = async (ownerId: string, pagination?: IPagination | undefined): Promise<IPaginatedList<IStudio>> => {
    const response = await GraphQLRequest({
      query: GET_USER_STUDIOS_QUERY,
      variables: {
        id: ownerId,
        limit: pagination?.limit ?? 100,
        nextToken: pagination?.nextToken
      },
      publicQuery: true
    })
    const gqlStudioItems = response.data.getUser.studios.items as GraphQLStudio[]
    const studios = gqlStudioItems.map(gqlStudio => new Studio(gqlStudio))
    return {
      items: studios,
      nextToken: response.data.getUser.studios.nextToken
    }
  }

  getAllStudios = async (pagination?: IPagination | undefined): Promise<IPaginatedList<IStudio>> => {
    const response = await GraphQLRequest({
      query: GET_ALL_STUDIOS_QUERY,
      variables: {
        limit: pagination?.limit ?? 10,
        nextToken: pagination?.nextToken,
        filter: {
          status: {
            eq: StudioStatus.ACTIVE
          }
        }
      },
      publicQuery: true
    })
    const gqlStudioItems = response.data.listStudios.items as GraphQLStudio[]
    const studios = gqlStudioItems.map(gqlStudio => new Studio(gqlStudio))
    return {
      items: studios,
      nextToken: response.data.listStudios.nextToken
    }
  }

  getStudioById = async (studioId: string): Promise<IStudio> => {
    const response = await GraphQLRequest({
      query: GET_STUDIO_BY_ID_QUERY,
      variables: {
        id: studioId
      },
      publicQuery: true
    })
    return new Studio(response.data.getStudio as GraphQLStudio)
  }

  delistStudio = async (studioId: string): Promise<void> => {
    await GraphQLRequest({
      query: UPDATE_STUDIO_MUTATION,
      variables: {
        input: {
          id: studioId,
          status: StudioStatus.DELISTED
        }
      }
    })
  }

  enlistStudio = async (studioId: string): Promise<void> => {
    const currentPendingActions = await this.getStudioPendingActions(studioId)
    if (currentPendingActions.length > 0) {
      throw new Error('Cannot enlist studio with pending actions')
    }
    await GraphQLRequest({
      query: UPDATE_STUDIO_MUTATION,
      variables: {
        input: {
          id: studioId,
          status: StudioStatus.ACTIVE
        }
      }
    })
  }

  getStudioOpenAvailabilities = async (studioId: string, date: Date): Promise<IDateRange[]> => {
    const response = await GraphQLRequest({
      query: GET_OPEN_AVAILABILITIES_QUERY,
      variables: {
        input: {
          studioId,
          date: date.toISOString()
        }
      },
      publicQuery: true
    })
    return response.data.getOpenAvailabilities.timeslots.map((timeslot: GraphQLDateTimeRange) => ({
      startDate: new Date(timeslot.startDate),
      endDate: new Date(timeslot.endDate)
    }))
  }

  getStudioAvailabilities = async (studioId: string): Promise<Availability> => {
    const response = await GraphQLRequest({
      query: GET_STUDIO_AVAILABILITY_QUERY,
      variables: {
        id: studioId
      },
      publicQuery: true
    })
    const studio = response.data.getStudio as GraphQLStudio
    const standardAvailability = Studio.parseStandardAvailability(response.data.getStudio.standardAvailability)
    const exceptions = studio.availabilityExceptions.map((exception: any) => {
      return {
        isAvailable: exception.isAvailable,
        timeRange: {
          startDate: new Date(exception.timeRange.startDate),
          endDate: new Date(exception.timeRange.endDate)
        }
      }
    })
    return {
      standardAvailability,
      exceptions
    }
  }

  requestStudioBooking = async (booking: RequestStudioBookingInput): Promise<IBooking> => {
    const input: GraphQLRequestBookingInput = {
      studioId: booking.studioId,
      startTime: booking.startTime.toISOString(),
      endTime: booking.endTime.toISOString()
    }
    const response = await GraphQLRequest({
      query: REQUEST_BOOKING_MUTATION,
      variables: {
        input
      }
    })
    const bookingResponse = await Booking.create({
      id: response.data.requestBooking.bookingId,
      status: BookingStatus.REQUESTED,
      hostUserId: response.data.requestBooking.hostUserId,
      requesterUserId: response.data.requestBooking.requesterUserId,
      studioId: booking.studioId,
      startTime: booking.startTime.toISOString(),
      endTime: booking.endTime.toISOString()
    })
    return bookingResponse
  }

  respondToBookingRequest = async (bookingId: string, accept: boolean): Promise<void> => {
    await GraphQLRequest({
      query: RESPOND_TO_BOOKING_REQUEST_MUTATION,
      variables: {
        input: {
          bookingId,
          accept
        }
      }
    })
  }

  getBookingPaymentUrl = async (bookingId: string, redirectUrl: string = '/bookings'): Promise<string> => {
    const input: GetBookingCheckoutSessionInput = {
      bookingId,
      successUrl: `${window.location.origin}${redirectUrl}`,
      cancelUrl: `${window.location.origin}${redirectUrl}`
    }
    const response = await GraphQLRequest({
      query: GET_BOOKING_CHECKOUT_SESSION_QUERY,
      variables: {
        input
      }
    })
    console.log(response)
    return response.data.getBookingCheckoutSession.checkoutUrl
  }

  private static readonly saveStudioPictures = async (studioId: string, pictures: File[]): Promise<S3Object[]> => {
    const savePictureFileInput = pictures.map((picture) => ({
      file: picture,
      filename: `${studioId}/${uuid()}.${StorageObject.getFileExtension(picture)}`
    }))
    const studioPictures = await StorageObject.saveFilesToStorage({
      folder: StorageFolder.STUDIO_PROFILE_PICTURE,
      files: savePictureFileInput
    })
    return studioPictures.map(picture => picture.s3Object)
  }
}
